/**
 * Opens images inside an article body in a full-screen viewer that can be
 * zoomed and panned. Written for the colour conversion charts: those are A3
 * sheets with dozens of small swatches, so "bigger" is not enough — the reader
 * has to be able to get close to a single row and move around.
 *
 * The product gallery cannot be reused here. Its lightbox is rendered from
 * Liquid media objects, while article images come out of the rich text editor
 * as plain <img> tags this element has to adopt at runtime.
 */
if (!customElements.get('article-image-zoom')) {
  customElements.define(
    'article-image-zoom',
    class ArticleImageZoom extends HTMLElement {
      constructor() {
        super();
        this.scale = 1;
        this.offsetLeft_ = 0;
        this.offsetTop_ = 0;
        this.pointers = new Map();
        this.pinchStart = null;
        this.onKeydown = this.onKeydown.bind(this);
      }

      static get MIN_SCALE() {
        return 1;
      }

      static get MAX_SCALE() {
        return 8;
      }

      connectedCallback() {
        const images = Array.from(this.querySelectorAll('img')).filter(
          (image) => !image.closest('a') && !image.hasAttribute('data-no-zoom')
        );
        if (!images.length) return;
        images.forEach((image) => this.enable(image));
      }

      enable(image) {
        image.classList.add('article-zoom__source');
        image.setAttribute('role', 'button');
        image.setAttribute('tabindex', '0');
        // The label has to survive an empty alt attribute, which the editor
        // writes by default when an image is inserted without a description.
        const label = image.getAttribute('alt') || this.dataset.zoomLabel || 'Zoom';
        image.setAttribute('aria-label', `${label} — ${this.dataset.zoomHint || ''}`.trim());
        image.addEventListener('click', () => this.open(image));
        image.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.open(image);
        });
      }

      /**
       * Shopify serves whatever width the editor happened to embed, which for a
       * dense chart is far too little once it is magnified. Asking the CDN for a
       * wide rendition keeps the swatch labels readable at high zoom.
       */
      highResolutionSource(image) {
        const source = image.currentSrc || image.src;
        try {
          const url = new URL(source, window.location.href);
          if (!/(^|\.)shopify\.com$|(^|\.)shopifycdn\.(com|net)$/.test(url.hostname)) return source;
          url.searchParams.set('width', '3000');
          return url.toString();
        } catch (error) {
          return source;
        }
      }

      buildViewer() {
        if (this.viewer) return this.viewer;

        const viewer = document.createElement('div');
        viewer.className = 'article-zoom';
        viewer.setAttribute('role', 'dialog');
        viewer.setAttribute('aria-modal', 'true');
        viewer.hidden = true;
        viewer.innerHTML = `
          <div class="article-zoom__backdrop" data-zoom-dismiss></div>
          <div class="article-zoom__stage" data-zoom-stage>
            <img class="article-zoom__image" alt="" data-zoom-image>
          </div>
          <div class="article-zoom__controls">
            <button type="button" class="article-zoom__button" data-zoom-out aria-label="${
              this.dataset.zoomOut || 'Zoom out'
            }">&minus;</button>
            <button type="button" class="article-zoom__button" data-zoom-reset>100%</button>
            <button type="button" class="article-zoom__button" data-zoom-in aria-label="${
              this.dataset.zoomIn || 'Zoom in'
            }">+</button>
            <button type="button" class="article-zoom__button article-zoom__button--close" data-zoom-dismiss aria-label="${
              this.dataset.zoomClose || 'Close'
            }">&times;</button>
          </div>
        `;
        document.body.appendChild(viewer);

        this.viewer = viewer;
        this.stage = viewer.querySelector('[data-zoom-stage]');
        this.image = viewer.querySelector('[data-zoom-image]');
        this.resetButton = viewer.querySelector('[data-zoom-reset]');

        viewer.querySelectorAll('[data-zoom-dismiss]').forEach((element) => {
          element.addEventListener('click', () => this.close());
        });
        viewer.querySelector('[data-zoom-in]').addEventListener('click', () => this.zoomBy(1.4));
        viewer.querySelector('[data-zoom-out]').addEventListener('click', () => this.zoomBy(1 / 1.4));
        this.resetButton.addEventListener('click', () => this.apply(1, 0, 0));

        this.stage.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
        this.stage.addEventListener('dblclick', this.onDoubleClick.bind(this));
        this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.stage.addEventListener('pointermove', this.onPointerMove.bind(this));
        ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => {
          this.stage.addEventListener(type, this.onPointerUp.bind(this));
        });

        return viewer;
      }

      open(image) {
        this.openedBy = image;
        const viewer = this.buildViewer();
        this.image.src = this.highResolutionSource(image);
        this.image.alt = image.getAttribute('alt') || '';
        this.apply(1, 0, 0);
        viewer.hidden = false;
        document.body.classList.add('article-zoom-open');
        document.addEventListener('keydown', this.onKeydown);
        this.resetButton.focus();
      }

      close() {
        if (!this.viewer || this.viewer.hidden) return;
        this.viewer.hidden = true;
        document.body.classList.remove('article-zoom-open');
        document.removeEventListener('keydown', this.onKeydown);
        if (this.openedBy) this.openedBy.focus();
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        // Keeps focus inside the viewer while it is open.
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.viewer.querySelectorAll('button'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }

      apply(scale, left, top) {
        this.scale = this.clamp(scale, ArticleImageZoom.MIN_SCALE, ArticleImageZoom.MAX_SCALE);
        // At 1× the image is centred by the stage, so panning is locked off.
        // Beyond that the offset is capped to the overflow the zoom produced,
        // which stops the drawing from being pushed out of sight.
        const bounds = this.stage.getBoundingClientRect();
        const overflowX = Math.max(0, (bounds.width * this.scale - bounds.width) / 2);
        const overflowY = Math.max(0, (bounds.height * this.scale - bounds.height) / 2);
        this.offsetLeft_ = this.clamp(left, -overflowX, overflowX);
        this.offsetTop_ = this.clamp(top, -overflowY, overflowY);
        this.image.style.transform = `translate(${this.offsetLeft_}px, ${this.offsetTop_}px) scale(${this.scale})`;
        this.stage.dataset.zoomed = this.scale > 1 ? 'true' : 'false';
        this.resetButton.textContent = `${Math.round(this.scale * 100)}%`;
      }

      zoomBy(factor) {
        this.apply(this.scale * factor, this.offsetLeft_ * factor, this.offsetTop_ * factor);
      }

      onWheel(event) {
        event.preventDefault();
        this.zoomBy(event.deltaY < 0 ? 1.15 : 1 / 1.15);
      }

      onDoubleClick(event) {
        event.preventDefault();
        this.scale > 1 ? this.apply(1, 0, 0) : this.zoomBy(2.5);
      }

      onPointerDown(event) {
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.stage.setPointerCapture(event.pointerId);
        if (this.pointers.size === 2) this.pinchStart = { distance: this.pointerDistance(), scale: this.scale };
      }

      onPointerMove(event) {
        if (!this.pointers.has(event.pointerId)) return;
        const previous = this.pointers.get(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 2 && this.pinchStart) {
          const ratio = this.pointerDistance() / this.pinchStart.distance;
          this.apply(this.pinchStart.scale * ratio, this.offsetLeft_, this.offsetTop_);
          return;
        }
        if (this.pointers.size !== 1 || this.scale <= 1) return;
        event.preventDefault();
        this.apply(this.scale, this.offsetLeft_ + (event.clientX - previous.x), this.offsetTop_ + (event.clientY - previous.y));
      }

      onPointerUp(event) {
        this.pointers.delete(event.pointerId);
        if (this.pointers.size < 2) this.pinchStart = null;
      }

      pointerDistance() {
        const [first, second] = Array.from(this.pointers.values());
        return Math.hypot(first.x - second.x, first.y - second.y);
      }
    }
  );
}
